import React, { useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Button,
    IconButton,
    Tooltip,
    Pagination,
    Box,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Email as EmailIcon,
    Search as SearchIcon,
    Refresh as ResetIcon,
    FileDownload as ExportIcon
} from '@mui/icons-material';
import styles from './Suppliers.module.css';
import supplierData from '../../data/supplierData.json';

// Import Icons, Tooltip, and IconButton
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

// Define the structure of a supplier
interface Supplier {
    id: number;
    supplierCode: string;
    name: string;
    contactPerson: string;
    email: string;
    phone: string;
    address: string;
    category: string;
    status: string;
    paymentTerms: string | null;
    totalOrders: number;
    totalSpent: number;
    lastOrderDate: string;
    rating: number;
    notes: string;
}

const Suppliers: React.FC = () => {
    const [suppliers, setSuppliers] = useState<Supplier[]>(supplierData);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = suppliers.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(suppliers.length / itemsPerPage);

    const handleSearch = () => {
        const filtered = supplierData.filter(supplier => {
            const matchesSearch = supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                supplier.supplierCode.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCategory = !categoryFilter || supplier.category === categoryFilter;
            const matchesStatus = !statusFilter || supplier.status === statusFilter;
            return matchesSearch && matchesCategory && matchesStatus;
        });
        setSuppliers(filtered);
        setCurrentPage(1);
    };

    const handleReset = () => {
        setSearchTerm('');
        setCategoryFilter('');
        setStatusFilter('');
        setSuppliers(supplierData);
        setCurrentPage(1);
    };

    const handleAddSupplier = () => {
        console.log('Adding new supplier');
    };

    const handleViewSupplier = (id: number) => {
        console.log('Viewing supplier:', id);
    };

    const handleEditSupplier = (id: number) => {
        console.log('Editing supplier:', id);
    };

    const handleDeleteSupplier = (id: number) => {
        console.log('Deleting supplier:', id);
    };

    const handleEmailSupplier = (email: string) => {
        console.log('Emailing supplier:', email);
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active':
                return styles.activeBadge;
            case 'inactive':
                return styles.inactiveBadge;
            case 'pending':
                return styles.pendingBadge;
            default:
                return '';
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    return (
        <div className={styles.suppliersContainer}>
            <div className={styles.header}>
                <Typography variant="h5" className={styles.title}>
                    Suppliers
                </Typography>
                <div className={styles.actions}>
                    <Tooltip title="Export Suppliers">
                        <IconButton onClick={() => console.log('Exporting suppliers')}>
                            <ExportIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddSupplier}
                    >
                        Add Supplier
                    </Button>
                </div>
            </div>

            <div className={styles.filterArea}>
                <div className={styles.filters}>
                    <div className={styles.filterGroup}>
                        <label>Search by Name or Code</label>
                        <TextField
                            className={styles.searchField}
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className={styles.filterGroup}>
                        <label>Category</label>
                        <FormControl className={styles.filterField} size="small">
                            <Select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                <MenuItem value="Electronics">Electronics</MenuItem>
                                <MenuItem value="Office Supplies">Office Supplies</MenuItem>
                                <MenuItem value="Furniture">Furniture</MenuItem>
                                <MenuItem value="Software">Software</MenuItem>
                                <MenuItem value="Services">Services</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className={styles.filterGroup}>
                        <label>Status</label>
                        <FormControl className={styles.filterField} size="small">
                            <Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Inactive">Inactive</MenuItem>
                                <MenuItem value="Pending">Pending</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                    <div className={styles.filterActions}>
                        <Button
                            className={styles.searchButton}
                            variant="contained"
                            onClick={handleSearch}
                        >
                            Search
                        </Button>
                        <Button
                            className={styles.resetButton}
                            variant="outlined"
                            onClick={handleReset}
                        >
                            Reset
                        </Button>
                    </div>
                </div>
            </div>

            <TableContainer component={Paper} className={styles.tableContainer}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Supplier Code</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Contact Person</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Total Orders</TableCell>
                            <TableCell>Total Spent</TableCell>
                            <TableCell>Last Order</TableCell>
                            <TableCell>Rating</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {currentItems.map((supplier, index) => (
                            <TableRow key={supplier.id}>
                                <TableCell>{indexOfFirstItem + index + 1}</TableCell>
                                <TableCell>{supplier.supplierCode}</TableCell>
                                <TableCell>{supplier.name}</TableCell>
                                <TableCell>{supplier.contactPerson}</TableCell>
                                <TableCell>{supplier.category}</TableCell>
                                <TableCell>
                                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(supplier.status)}`}>
                                        {supplier.status}
                                    </span>
                                </TableCell>
                                <TableCell>{supplier.totalOrders}</TableCell>
                                <TableCell>{formatCurrency(supplier.totalSpent)}</TableCell>
                                <TableCell>{supplier.lastOrderDate}</TableCell>
                                <TableCell>{supplier.rating.toFixed(1)}</TableCell>
                                <TableCell>
                                    <div className={styles.actionButtons}>
                                        <Tooltip title="View Details">
                                            <IconButton onClick={() => handleViewSupplier(supplier.id)}>
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit Supplier">
                                            <IconButton onClick={() => handleEditSupplier(supplier.id)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Supplier">
                                            <IconButton onClick={() => handleDeleteSupplier(supplier.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Send Email">
                                            <IconButton onClick={() => handleEmailSupplier(supplier.email)}>
                                                <EmailIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box className={styles.paginationContainer}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(event, value) => setCurrentPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                />
            </Box>
        </div>
    );
};

export default Suppliers; 